import Seo from "../components/Seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { MEDIA } from "../lib/media";
import ProductCard from "../components/ProductCard";
import FeaturedCard from "../components/FeaturedCard";
import CategoryTile from "../components/CategoryTile";
import CTASection from "../components/CTASection";
import Reveal from "../components/Reveal";
import ReviewsSlider from "../components/ReviewsSlider";
import HomeSlider from "../components/HomeSlider";
import {
  ShieldCheck, Truck, Gift, Palette, Star, MessageCircle, Sparkles, Camera, Award,
} from "lucide-react";

const STATS = [
  { label: "Над 400 произведения" },
  { label: "Авторски картини" },
  { label: "Доставка до адрес" },
  { label: "Консултация при избор" },
];

const MOODS = [
  { title: "Спокойствие", line: "Меки тонове за отмора", img: MEDIA.interiorInspiration, q: "Пейзажи" },
  { title: "Лукс", line: "Злато и дълбочина", img: resolveImg(MEDIA.abstract), q: "Абстрактни" },
  { title: "Море", line: "Свежест и простор", img: resolveImg(MEDIA.seascape), q: "Морски пейзажи" },
  { title: "Топлина", line: "Уют за дома", img: MEDIA.livingRoom, q: "Натюрморти" },
  { title: "Минимализъм", line: "Чисти линии", img: MEDIA.bedroom, q: "Градски пейзажи" },
  { title: "Цвят и енергия", line: "Смели акценти", img: MEDIA.office, q: "Животни" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then(({ data }) => setFeatured(data.items));
    api.get("/products", { params: { sort: "price_desc", limit: 8 } }).then(({ data }) => setPopular(data.items));
    api.get("/reviews").then(({ data }) => setReviews(data));
    try { setRecent(JSON.parse(localStorage.getItem("pa_recent") || "[]")); } catch {}
  }, []);

  const showRecent = recent.length >= 4;
  const secondaryItems = showRecent ? recent : popular;

  return (
    <div data-testid="home-page">
      <Seo path="/" description="PenichArt – оригинални ръчно рисувани картини с маслени и акрилни бои от Силва Пенева. Картини за дом, офис и подарък. Доставка в цяла България." />
      {/* ---------- HERO ---------- */}
      <section className="relative bg-ivory">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 items-center gap-8 px-4 sm:px-6 pt-8 pb-10 lg:py-0">
          <div className="order-2 lg:order-1 lg:py-16">
            <Reveal>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-navy">
                Картини, които превръщат дома в галерия
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="gold-line my-5" />
              <p className="text-charcoal/70 text-base md:text-lg max-w-md leading-relaxed">
                Оригинални произведения с характер, подбрани за стилни интериори, специални подаръци и хора с усет към красивото.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 mt-7">
                <Link to="/kartini" className="btn-gold px-7 py-3.5 rounded-sm text-center" data-testid="hero-primary-cta">
                  Разгледай колекцията
                </Link>
                <Link to="/kartini?view=collections" className="btn-navy-outline px-7 py-3.5 rounded-sm text-center" data-testid="hero-secondary-cta">
                  Избери по стил
                </Link>
              </div>
              <div className="flex items-center gap-2 text-sm text-charcoal/60 mt-5">
                <ShieldCheck size={16} className="text-gold" />
                <span>Оригинални картини • Сигурна доставка • Подходящи за подарък</span>
              </div>
            </Reveal>
          </div>

          <div className="order-1 lg:order-2 relative">
            <Reveal delay={0.15}>
              <div className="overflow-hidden">
                <img src={MEDIA.hero} alt="PenichArt галерия в дома" className="w-full h-[300px] sm:h-[440px] lg:h-[600px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* conversion strip */}
        <div className="border-t border-stone bg-ivory">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-stone">
            {STATS.map((s) => (
              <div key={s.label} className="py-3.5 px-3 text-center text-xs sm:text-sm text-navy font-medium flex items-center justify-center gap-2">
                <Sparkles size={14} className="text-gold hidden sm:block" />{s.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SMART SHOPPING ENTRY ---------- */}
      <ReviewsSlider />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="smart-entry">
        <Reveal className="text-center mb-8">
          <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">Намерете лесно</p>
          <h2 className="font-serif text-3xl md:text-4xl text-navy">Намерете картина според вашия дом</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          <CategoryTile testid="entry-living" image={MEDIA.livingRoom} subtitle="Интериор" title="За дневна" to="/kartini?room=Дневна" />
          <CategoryTile testid="entry-bedroom" image={MEDIA.bedroom} subtitle="Интериор" title="За спалня" to="/kartini?room=Спалня" />
          <CategoryTile testid="entry-office" image={MEDIA.office} subtitle="Интериор" title="За офис" to="/kartini?room=Офис" />
          <CategoryTile testid="entry-sea" image={resolveImg(MEDIA.seascape)} subtitle="Стил" title="Морски пейзажи" to="/kartini?search=море" />
          <CategoryTile testid="entry-abstract" image={resolveImg(MEDIA.abstract)} subtitle="Стил" title="Абстрактни" to="/kartini?category=абстрактни-картини" />
          <CategoryTile testid="entry-gift" image={MEDIA.gift} subtitle="Повод" title="Подарък за нов дом" to="/podaratsi" />
        </div>
      </section>

      {/* ---------- FEATURED ---------- */}
      <section className="bg-white border-y border-stone py-12 md:py-16" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-9">
            <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">Селекция на галерията</p>
            <h2 className="font-serif text-3xl md:text-4xl text-navy">Подбрани картини</h2>
            <p className="text-charcoal/60 mt-2.5 max-w-xl mx-auto">
              Произведения, които носят настроение, дълбочина и завършеност на интериора.
            </p>
          </Reveal>
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => <FeaturedCard key={p.id} product={p} />)}
          </div>
          <div className="sm:hidden flex gap-4 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2 snap-x">
            {featured.map((p) => (
              <div key={p.id} className="min-w-[78%] snap-start"><FeaturedCard product={p} /></div>
            ))}
          </div>
          <div className="text-center mt-9">
            <Link to="/kartini" className="btn-navy-outline px-8 py-3 rounded-sm" data-testid="featured-view-all">Виж всички картини</Link>
          </div>
        </div>
      </section>

      {/* ---------- POPULAR / RECENTLY VIEWED ---------- */}
      {secondaryItems.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="secondary-products">
          <Reveal className="flex items-end justify-between mb-8">
            <div>
              <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">{showRecent ? "За вас" : "Любими на клиентите"}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-navy">{showRecent ? "Разгледани наскоро" : "Популярни избори"}</h2>
            </div>
            <Link to="/kartini" className="hidden sm:inline text-sm text-gold hover:text-gold-dark underline">Виж всички</Link>
          </Reveal>
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-5">
            {secondaryItems.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="sm:hidden flex gap-4 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2 snap-x">
            {secondaryItems.slice(0, 8).map((p) => (
              <div key={p.id} className="min-w-[75%] snap-start"><ProductCard product={p} /></div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- CONSULTATION CTA ---------- */}
      <section className="bg-navy" data-testid="consultation-cta">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-14 grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
          <div className="w-14 h-14 border border-gold flex items-center justify-center text-gold shrink-0 mx-auto md:mx-0">
            <Camera size={26} strokeWidth={1.4} />
          </div>
          <div className="text-center md:text-left">
            <h3 className="font-serif text-2xl md:text-3xl text-white leading-snug">Изпратете снимка на интериора и ще ви предложим подходяща картина</h3>
            <p className="text-ivory/70 mt-2">Безплатна консултация при избор — за дом, офис или подарък.</p>
          </div>
          <Link to="/kontakti" className="btn-gold px-7 py-3.5 rounded-sm text-center whitespace-nowrap" data-testid="consultation-btn">
            Изпрати снимка
          </Link>
        </div>
      </section>

      {/* ---------- VALUE PROPS ---------- */}
      <section className="bg-stone py-12 md:py-16" data-testid="value-props">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-9">
            <h2 className="font-serif text-3xl md:text-4xl text-navy">Защо PenichArt?</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-7">
            {[
              { icon: Palette, t: "Оригинални произведения", d: "Всяка картина носи собствен характер и история." },
              { icon: Sparkles, t: "Подбрани за интериор", d: "Лесно откривате картина по стил, цвят, размер и помещение." },
              { icon: Truck, t: "Сигурна доставка", d: "Опаковане с внимание и доставка до вашия адрес." },
              { icon: Gift, t: "Перфектен подарък", d: "Изкуство с емоция за дом, офис или специален повод." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08} className="text-center">
                <div className="w-14 h-14 mx-auto border border-gold flex items-center justify-center text-gold mb-4">
                  <v.icon size={24} strokeWidth={1.4} />
                </div>
                <h3 className="font-serif text-xl text-navy mb-1.5">{v.t}</h3>
                <p className="text-sm text-charcoal/65 leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- MOODS ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="mood-section">
        <Reveal className="text-center mb-8">
          <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">По усещане</p>
          <h2 className="font-serif text-3xl md:text-4xl text-navy">Изберете усещането</h2>
        </Reveal>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 snap-x">
          {MOODS.map((m) => (
            <Link key={m.title} to={`/kartini?search=${encodeURIComponent(m.q)}`}
                  className="cat-tile group relative min-w-[240px] sm:min-w-[280px] aspect-[3/4] overflow-hidden snap-start"
                  data-testid={`mood-${m.title}`}>
              <img src={m.img} alt={m.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-serif text-2xl text-white">{m.title}</h3>
                <p className="text-ivory/80 text-sm">{m.line}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------- INTERIOR INSPIRATION ---------- */}
      <section className="bg-white border-y border-stone" data-testid="inspiration-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 items-center gap-10 px-4 sm:px-6 py-12 md:py-16">
          <Reveal>
            <img src={MEDIA.interiorInspiration} alt="Картина в интериор" className="w-full h-[300px] md:h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">Вдъхновение</p>
            <h2 className="font-serif text-3xl md:text-4xl text-navy leading-tight">
              Вижте как една картина променя пространството
            </h2>
            <p className="text-charcoal/70 mt-4 leading-relaxed">
              Правилната картина добавя дълбочина, емоция и идентичност на дома. Тя превръща стената в акцент,
              а стаята – в място с характер и присъствие.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <img src={MEDIA.emptyWall} alt="Преди" className="w-full h-36 object-cover grayscale" />
                <p className="text-xs text-center text-charcoal/50 mt-2">Преди</p>
              </div>
              <div>
                <img src={MEDIA.interiorInspiration} alt="След" className="w-full h-36 object-cover" />
                <p className="text-xs text-center text-gold mt-2 font-medium">След</p>
              </div>
            </div>
            <Link to="/kartini" className="inline-block btn-gold px-7 py-3 rounded-sm mt-7" data-testid="inspiration-cta">
              Открий картина за моя дом
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- ARTIST STORY ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="artist-section">
        <div className="grid lg:grid-cols-2 items-center gap-10 lg:gap-14">
          <Reveal className="lg:order-2">
            <div className="relative">
              <img src={MEDIA.artist} alt="Художник Силва Пенева в ателието" className="w-full h-[360px] md:h-[520px] object-cover" />
              <div className="absolute -bottom-4 -left-4 hidden md:flex items-center gap-2 bg-ivory border border-gold px-4 py-2.5">
                <Award size={18} className="text-gold" />
                <span className="text-sm text-navy font-medium">Сертификат за автентичност</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:order-1">
            <p className="text-gold uppercase tracking-[0.25em] text-xs mb-2.5">За художника</p>
            <h2 className="font-serif text-3xl md:text-4xl text-navy leading-tight">Изкуство с личен почерк</h2>
            <div className="gold-line my-5" />
            <p className="text-charcoal/75 mt-2 leading-relaxed text-lg">
              Зад всяка картина стои художничката Силва Пенева — и часове търпение, емоция и любов към детайла.
            </p>
            <p className="text-charcoal/65 mt-4 leading-relaxed">
              Творбите са 100% ръчно рисувани с маслени бои и са единствени по рода си. Те не са просто декорация –
              те носят настроение, спомен и присъствие, които остават в дома за поколения.
            </p>
            <Link to="/za-avtora" className="inline-block btn-navy-outline px-7 py-3 rounded-sm mt-7" data-testid="artist-cta">
              Научи повече за художника
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- GIFT ---------- */}
      <section className="bg-stone py-12 md:py-16" data-testid="gift-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 items-center gap-10">
          <Reveal>
            <img src={MEDIA.gift} alt="Подарък – картина" className="w-full h-[300px] md:h-[400px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl md:text-4xl text-navy leading-tight">Подарък, който остава</h2>
            <p className="text-charcoal/70 mt-3 leading-relaxed">
              Картина за нов дом, рожден ден, юбилей, сватба или специален човек.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-5">
              {[["До 200 €", "/kartini?max=200"], ["До 500 €", "/kartini?max=500"],
                ["За нов дом", "/podaratsi"], ["За офис", "/kartini?room=Офис"],
                ["Морски пейзажи", "/kartini?search=море"], ["Абстрактни", "/kartini?category=абстрактни-картини"]].map(([c, to]) => (
                <Link key={c} to={to} className="text-sm border border-navy/30 text-navy px-4 py-2 hover:bg-navy hover:text-white transition-colors" data-testid={`gift-chip-${c}`}>
                  {c}
                </Link>
              ))}
            </div>
            <Link to="/podaratsi" className="inline-block btn-gold px-7 py-3 rounded-sm mt-7" data-testid="gift-cta">
              Разгледай подаръчни предложения
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- TRUST / REVIEWS ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="reviews-section">
        <Reveal className="text-center mb-9">
          <h2 className="font-serif text-3xl md:text-4xl text-navy">Доверие и спокойствие при покупка</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {reviews.slice(0, 4).map((r, i) => (
            <Reveal key={r.id || i} delay={(i % 2) * 0.08}>
              <div className="bg-white border border-stone p-6 h-full">
                <div className="flex gap-0.5 text-gold mb-3">
                  {Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} size={15} fill="#C6A45C" />)}
                </div>
                <p className="font-serif text-lg text-navy leading-relaxed">„{r.text}"</p>
                <p className="text-sm text-charcoal/50 mt-4">{r.name} · {r.city}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: MessageCircle, t: "Консултация при избор" },
            { icon: ShieldCheck, t: "Сигурно плащане" },
            { icon: Camera, t: "Връзка преди покупка" },
          ].map((b) => (
            <div key={b.t} className="flex items-center gap-3 justify-center bg-ivory border border-stone py-4 px-3 text-navy">
              <b.icon size={18} className="text-gold" /> <span className="text-sm font-medium">{b.t}</span>
            </div>
          ))}
        </div>
      </section>

      <HomeSlider />

      <CTASection />
    </div>
  );
}
